import {
  DatePicker,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Label,
  Search,
} from "@/components/ui";
import { useSnomedSearch } from "@/network/useOntologies";
import {
  constants,
  type Surgery,
  getCodeValue,
  getDisplay,
} from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";
import { SubmitCancelButtons } from "../../components/SubmitCancelButtons";
import { surgicalHistoryFormSchema } from "../schemas";

interface SurgicalHistoryFormProps {
  surgicalHistory?: Surgery;
  onSubmit: (data: z.infer<typeof surgicalHistoryFormSchema>) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

const PROCEDURE_SNOMED_PARENT_CODE = "71388002";

export const SurgicalHistoryForm = ({
  surgicalHistory,
  onSubmit,
  onCancel,
  isSubmitting,
}: SurgicalHistoryFormProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { data: surgeryCodes, isLoading: isLoadingSurgeryCodes } =
    useSnomedSearch({
      searchTerm,
      eclExpression: `<< ${PROCEDURE_SNOMED_PARENT_CODE}`,
    });
  const form = useForm<z.infer<typeof surgicalHistoryFormSchema>>({
    resolver: zodResolver(surgicalHistoryFormSchema),
    defaultValues: {
      performedDate: surgicalHistory?.performedDateTime
        ? new Date(surgicalHistory.performedDateTime)
        : undefined,
      procedureCode: surgicalHistory?.code
        ? getCodeValue(surgicalHistory.code, constants.SNOMED_SYSTEM_URL)
        : undefined,
      procedureCodeDisplay: surgicalHistory?.code
        ? getDisplay(surgicalHistory.code)
        : undefined,
      procedureCodeSystem: constants.SNOMED_SYSTEM_URL,
    },
  });
  return (
    <div>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-2 px-3 py-2"
      >
        <Label>{surgicalHistory?.id ? "Edit" : "Add"} Surgical History</Label>
        <div className="flex flex-row gap-2">
          <Controller
            control={form.control}
            name="performedDate"
            render={({ field, fieldState }) => (
              <Field className="flex-1">
                <DatePicker
                  autoFocus={!surgicalHistory?.id}
                  label="Date"
                  date={field.value}
                  setDate={field.onChange}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
          <Controller
            control={form.control}
            name="procedureCode"
            disabled={Boolean(surgicalHistory?.id)}
            render={({ fieldState }) => (
              <Field className="flex-1">
                <FieldLabel htmlFor="procedureCode">Type</FieldLabel>
                {/* If the surgical history is already created, we don't
                want to allow the user to change the procedure code */}
                {surgicalHistory?.id ? (
                  <Input
                    value={
                      surgicalHistory.code && getDisplay(surgicalHistory.code)
                    }
                    disabled
                  />
                ) : (
                  <Search
                    placeholder="Search for a procedure"
                    options={
                      surgeryCodes?.map((code) => ({
                        label: code.display,
                        value: code.code,
                        metadata: {
                          system: code.system,
                        },
                      })) || []
                    }
                    isLoading={isLoadingSurgeryCodes}
                    onInputChange={(input) => {
                      setSearchTerm(input);
                    }}
                    onItemSelected={(item) => {
                      form.setValue("procedureCode", item.value as string);
                      form.setValue("procedureCodeDisplay", item.label);
                      form.setValue(
                        "procedureCodeSystem",
                        (item.metadata as { system?: string })?.system || "",
                      );
                    }}
                  />
                )}
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </div>
        <SubmitCancelButtons isSaving={isSubmitting} onCancel={onCancel} />
      </form>
    </div>
  );
};
